import { debounce, updateArrayItemOrder, uuid } from '@/Utility/utils';
import type { Media_CreateMediaItem_Mutation$data } from '@/graphql/mutations/Media.mutation';
import type { DragEndEvent } from '@dnd-kit/core';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import { Box, Button, Stack } from '@mui/material';
import React, { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { SortableList } from '../dnd/SortableList';
import DragDropWrapper, { type AllowedTypes } from './DragDropWrapper/DragDropWrapper';

type RepeaterRowProps<State> = {
  index: number;
  uuid: string;
  onStateChange: (idx: number, row: State) => void;
  initState: State;
  rowEl: (props: RowElProps<State>) => React.JSX.Element;
  removeRow: (index: number) => void;
  disabled?: boolean;
  isDragging?: boolean;
};

const RepeaterRow = <T,>({
  index,
  removeRow,
  rowEl,
  onStateChange,
  initState,
  uuid,
  isDragging,
  disabled = false,
}: RepeaterRowProps<T>) => {
  const Row = rowEl;

  return (
    <Stack
      key={uuid}
      direction="row"
      justifyContent="space-between"
      alignItems="center"
      sx={{ border: '1px solid #e3e3e3', padding: 1, cursor: 'grab' }}
    >
      <Row
        index={index}
        onStateChange={onStateChange}
        initState={initState}
        disabled={disabled}
        isDragging={isDragging || false}
      />
      <Button
        disabled={disabled || isDragging}
        startIcon={<RemoveCircleIcon />}
        className="z-btn-2 red"
        onClick={() => {
          removeRow(index);
        }}
        size="small"
        sx={{ height: '100%' }}
      >
        Remove
      </Button>
    </Stack>
  );
};

// memoize the row to prevent re-rendering
const RepeaterRowMemo = React.memo(
  RepeaterRow,
  (prevProps, nextProps) =>
    prevProps.uuid === nextProps.uuid &&
    prevProps.index === nextProps.index &&
    prevProps.disabled === nextProps.disabled &&
    prevProps.isDragging === nextProps.isDragging &&
    JSON.stringify(prevProps?.initState) === JSON.stringify(nextProps?.initState)
);

// forward the ref to the parent
export type RepeaterInputRef<State> = {
  // get data upon request
  getData: () => State[];
  // get formatted data upon request
  getFormattedData: () => State[];
  // reset the repeater state
  reset: (newInitState?: State[]) => void;
};

export type RowElProps<State> = {
  index: number;
  onStateChange: (idx: number, rowState: State) => void;
  initState?: State;
  disabled?: boolean;
  isDragging: boolean;
};

type Props<State> = {
  style?: React.CSSProperties;
  repeaterStyle?: React.CSSProperties;
  // the component to render for each row
  rowEl: (props: RowElProps<State>) => React.JSX.Element;
  // when the state changes
  onStateChange?: (data: State[]) => void;
  // initial data for all the rows
  initState?: State[];
  // limit the number of rows that can be added
  limit?: number;
  mimeTypes?: AllowedTypes[];
  unsupportedMessage?: string;
  onDragChange?: (setState: React.Dispatch<React.SetStateAction<State[]>>, data: string | File) => void;
  onDragUpload?: (
    setState: React.Dispatch<React.SetStateAction<State[]>>,
    data: Media_CreateMediaItem_Mutation$data
  ) => void;
  dragMessage?: string;
  // disabled
  disabled?: boolean;
  // showAlertOnDelete?: boolean; @TODO: implement this
};

const Repeater = <T,>(
  { rowEl, mimeTypes = ['text/plain'], disabled = false, ...props }: Props<T>,
  dataRef: React.ForwardedRef<RepeaterInputRef<T>>
) => {
  // init state can only be used by the rows once
  // in case the row is added and removed, the init state should not be used again
  const [canUseInitState, setCanUseInitState] = useState(true);
  const [initRepeaterState, setInitRepeaterState] = useState({
    rows: props?.initState?.map(() => ({ id: uuid() })) || [],
    data: props?.initState || [],
  });

  // set the initial state of the rows and the data
  const [rows, setRows] = useState<Array<{ id: string }>>(initRepeaterState.rows);
  const [data, setData] = useState<T[]>(initRepeaterState.data);

  // use ref to keep track of the last row and scroll to it
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (props?.initState && canUseInitState) {
      const newInitRepeaterState = {
        rows: props?.initState.map(() => ({ id: uuid() })),
        data: props?.initState,
      };
      setRows(newInitRepeaterState.rows);
      setData(newInitRepeaterState.data);
      setInitRepeaterState(newInitRepeaterState);

      setTimeout(() => {
        setCanUseInitState(false);
      }, 100);
    }
  }, [props?.initState]);

  // debounce the state change callback -> only call it once every 500ms
  const debounceOnPropsStateChange = useCallback(
    debounce((newData: T[]) => {
      props?.onStateChange?.(newData);
    }, 500),
    [debounce, props?.onStateChange]
  );

  const addRow = (addEmptyRow?: boolean) => {
    setRows([...rows, { id: uuid() }]);
    if (addEmptyRow) setData([...data, null as T]);

    // scroll the last row into view
    setTimeout(() => {
      scrollRef.current?.lastElementChild?.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }, 200);
  };

  const removeRow = (index: number) => {
    setRows((prev) => {
      const newRows = [...prev];
      newRows.splice(index, 1);
      return newRows;
    });

    setData((prev) => {
      const newData = [...prev];
      newData.splice(index, 1);

      // call the parent callback
      debounceOnPropsStateChange(newData);

      return newData;
    });
  };

  const onDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!active.id || !over?.id || active.id === over.id) return;

    const oldIndex = active?.data?.current?.sortable?.index;
    const newIndex = over?.data?.current?.sortable?.index;

    setRows((prev) => updateArrayItemOrder([...prev], oldIndex, newIndex));
    setData((prev) => {
      const newData = updateArrayItemOrder([...prev], oldIndex, newIndex);

      debounceOnPropsStateChange(newData);

      return newData;
    });
  };

  const onStateChange = (index: number, row: T | unknown) => {
    setData((prev) => {
      const newData = [...prev];

      newData[index] = row as T;

      // call the parent callback
      debounceOnPropsStateChange(newData);

      return newData;
    });
  };

  useImperativeHandle(
    dataRef,
    () => {
      return {
        getData: () => data || [],
        getFormattedData: () => {
          const tmpData = data;

          // ensure each array has all the data set
          // if all the keys are emtpy strings or null, remove the item
          tmpData.map((item: T, idx) => {
            try {
              if (
                // @ts-ignore
                Object.keys(item).every((key) => {
                  // @ts-ignore
                  return ['', null, 'https://'].includes(item?.[key] as string);
                })
              )
                tmpData.splice(idx, 1);
            } catch {
              if (!item) tmpData.splice(idx, 1);
            }
          });
          return !!tmpData.length ? tmpData : [];
        },
        reset: (newInitData) => {
          setCanUseInitState(true);
          let rows: Array<{ id: string }> = initRepeaterState.rows;
          let data: T[] = initRepeaterState.data;
          // Handle save then reset again
          if (!!newInitData) {
            rows = newInitData?.map(() => ({ id: uuid() })) || [];
            data = newInitData || [];
            setInitRepeaterState({
              rows,
              data,
            });
          }

          setRows(rows);
          setData(data);
          setTimeout(() => {
            setCanUseInitState(false);
          }, 100);
        },
      };
    },
    [data]
  );

  return (
    <DragDropWrapper
      mimeTypes={mimeTypes}
      disabled={
        (!props?.onDragChange && !props.onDragUpload) || (props?.limit && rows.length >= props.limit) || disabled
      }
      onChange={(value) => {
        addRow();
        props?.onDragChange?.(setData, value);
      }}
      fileCallback={(newData) => {
        addRow();
        props?.onDragUpload?.(setData, newData as Media_CreateMediaItem_Mutation$data);
      }}
      unsupportedMessage={props?.unsupportedMessage}
      infoMessage={props?.dragMessage}
    >
      <Box style={props?.style} sx={{ border: '1px solid #e3e3e3', padding: '1em !important' }}>
        <Stack
          ref={scrollRef}
          className="testing scroll ref"
          style={props?.repeaterStyle}
          direction="column"
          sx={{ padding: 1, mb: 1 }}
        >
          <SortableList items={rows} onDragEnd={onDragEnd} disabled={disabled}>
            {(row, isDragging, idx) => (
              <RepeaterRowMemo
                key={row.id}
                disabled={disabled}
                isDragging={isDragging}
                index={idx}
                onStateChange={onStateChange}
                initState={(() => {
                  if (canUseInitState && props?.initState && props.initState?.[idx]) {
                    return props.initState[idx];
                  }
                  if (!!data?.[idx] && data?.[idx]) {
                    return data[idx];
                  }
                  return null;
                })()}
                // @ts-ignore
                rowEl={rowEl}
                removeRow={removeRow}
                uuid={row.id}
              />
            )}
          </SortableList>
        </Stack>
        {props?.limit && rows.length >= props.limit ? null : (
          <Button
            disabled={disabled}
            className="z-btn-3"
            onClick={() => {
              addRow(true);
            }}
            fullWidth
            size="small"
          >
            Add
          </Button>
        )}
      </Box>
    </DragDropWrapper>
  );
};

export type { Props as RepeaterProps };

export default forwardRef(Repeater);
